import { getRentBill, getRentPaymentTransaction } from "../../utils/rentPaymentTransaction";
import { canRenewRent, renewalUnavailableMessage } from "../../utils/canRenewRent";
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DashboardLayout } from "../../components/layouts/DashboardLayout";
import styles from "./RenewRent.module.css";
import {
  ArrowLeft,
  RotateCw,
  CalendarDays,
  UsersRound,
  MapPin,
  Wallet,
  Image as ImageIcon,
} from "lucide-react";
import { addMonths, addYears, parseISO } from "date-fns";
import {
  getRenewalQuote,
  createRentRenewal,
  getSinglePropertyTransaction,
  getTransactionByReference,
} from "../../services/krent.service";
import PaymentMethodModal from "../../components/PaymentMethodModal";
import ktoast from "../../services/toast.service";

const formatNaira = (amount: number | undefined | null): string =>
  amount == null || !Number.isFinite(Number(amount))
    ? "—"
    : `₦${Number(amount).toLocaleString("en-NG")}`;

const fmtDate = (d: string | undefined | null): string => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

interface RenewalBill {
  annualRent?: number;
  rentPerYear?: number;
  years?: number;
  rentTotal?: number;
  serviceCharge?: number;
  serviceChargeTotal?: number;
  cautionFee?: number;
  agencyFee?: number;
  legalFee?: number;
  total?: number;
  currency?: string;
  maxRentalYears?: number;
  months?: number;
  maxRentalMonths?: number;
  monthlyRent?: number;
  administrativeFee?: number;
  cycle?: "yearly" | "monthly";
}

interface RenewalQuote {
  status?: string;
  // Booking / property info
  bookingId?: string;
  transactionId?: string;
  renewalTransactionId?: string;
  propertyName?: string;
  propertyTitle?: string;
  location?: string;
  city?: string;
  state?: string;
  imageUrl?: string;
  coverImage?: string;
  landlordName?: string;
  landlordFirstname?: string;
  landlordLastname?: string;
  tenantName?: string;
  tenantFirstname?: string;
  tenantLastname?: string;

  // Dates
  newArrivalDate?: string;
  newDepartureDate?: string;
  renewalStartDate?: string;
  renewalEndDate?: string;
  arrivalDate?: string;
  departureDate?: string;
  arrival_date?: string;
  departure_date?: string;

  // Pricing
  annualRent?: number;
  rentPerYear?: number;
  cautionFee?: number;
  serviceFee?: number;
  serviceCharge?: number;
  agencyFee?: number;
  total?: number;
  totalAmount?: number;
  renewalCommencementDate?: string;
  currentDepartureDate?: string;
  bill?: RenewalBill | RenewalBill[];

  // Nested objects the API might return
  listing?: {
    title?: string;
    coverImage?: string;
    location?: { city?: string; state?: string };
    [key: string]: any;
  };
  owner?: { firstname?: string; lastname?: string; [key: string]: any };
  renter?: { firstname?: string; lastname?: string; [key: string]: any };
}

export default function RenewRent() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const quoteRequest = useRef(0);
  const creatingRef = useRef(false);
  const [creating, setCreating] = useState(false);
  const [renewal, setRenewal] = useState<{ transactionId: string; referenceId: string; term: number; total: number } | null>(null);
  const [unresolvedRenewal, setUnresolvedRenewal] = useState(false);
  const [creationError, setCreationError] = useState("");
  const [booking, setBooking] = useState<RenewalQuote | null>(null);
  const [rentTerm, setRentTerm] = useState(1);
  const [rentCycle, setRentCycle] = useState<"yearly" | "monthly" | null>(null);
  const rentIsMonthly = rentCycle === "monthly";
  const rentUnit = rentIsMonthly ? "month" : "year";
  const [quote, setQuote] = useState<RenewalQuote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [propertyImageFailed, setPropertyImageFailed] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const fetchQuote = useCallback(
    async (term: number, unit: "years" | "months") => {
      if (!id) return;
      const request = ++quoteRequest.current;
      setLoading(true);
      setError(null);
      setQuote(null);
      try {
        const res: any = await getRenewalQuote(id, term, unit);
        if (request !== quoteRequest.current) return;
        // API may return data nested under res.data or at root
        if (res?.success === false || !(res?.data ?? res))
          throw new Error(res?.message || "Renewal quote is unavailable.");
        const nextQuote = res?.data ?? res;
        setQuote(nextQuote);
        const nextBill = Array.isArray(nextQuote?.bill) ? nextQuote.bill[0] : nextQuote?.bill;
        if (nextBill?.cycle) setRentCycle(nextBill.cycle);
        setPropertyImageFailed(false);
      } catch (err: any) {
        if (request !== quoteRequest.current) return;
        const msg =
          err?.errors?.[0]?.message ||
          err?.message ||
          "Could not load renewal quote. Please try again.";
        setError(msg);
        ktoast.error(msg);
      } finally {
        if (request === quoteRequest.current) setLoading(false);
      }
    },
    [id],
  );

  useEffect(() => {
    fetchQuote(rentTerm, rentIsMonthly ? "months" : "years");
  }, [fetchQuote, rentTerm, rentIsMonthly]);

  useEffect(() => {
    if (!id) return;
    const controller = new AbortController();
    setBooking(null);
    const request = /^[a-f0-9]{24}$/i.test(id)
      ? getSinglePropertyTransaction(id, controller.signal)
      : getTransactionByReference(id);
    request
      .then((res: any) => {
        if (!controller.signal.aborted) setBooking(res?.data ?? res);
      })
      .catch(() => {
        if (!controller.signal.aborted)
          ktoast.error("Could not load property details for this renewal.");
      });
    return () => controller.abort();
  }, [id]);

  const bill = Array.isArray(quote?.bill) ? quote?.bill[0] : quote?.bill;
  const quotedTerm = (rentIsMonthly ? bill?.months : bill?.years) ?? rentTerm;
  const maxTerm = rentIsMonthly ? bill?.maxRentalMonths : bill?.maxRentalYears;
  const incrementTerm = () =>
    setRentTerm((t) => (maxTerm == null ? t + 1 : Math.min(maxTerm, t + 1)));
  const decrementTerm = () => setRentTerm((t) => Math.max(1, t - 1));
  const details = { ...booking, ...quote };

  // Normalise the various field names the API might use
  const propertyName =
    details?.propertyName ||
    details?.propertyTitle ||
    details?.listing?.title ||
    "—";
  const locationStr =
    details?.location ||
    (details?.city && details?.state
      ? `${details.city}, ${details.state}`
      : null) ||
    (details?.listing?.location
      ? `${details.listing.location.city ?? ""}, ${details.listing.location.state ?? ""}`.replace(
          /^,\s*|,\s*$/g,
          "",
        )
      : null) ||
    "—";
  const imageUrl =
    details?.imageUrl ||
    details?.coverImage ||
    details?.listing?.coverImage ||
    "";
  const landlordName =
    details?.landlordName ||
    (details?.landlordFirstname
      ? `${details.landlordFirstname} ${details.landlordLastname ?? ""}`.trim()
      : null) ||
    (details?.owner
      ? `${details.owner.firstname ?? ""} ${details.owner.lastname ?? ""}`.trim()
      : null) ||
    "—";
  const tenantName =
    details?.tenantName ||
    (details?.tenantFirstname
      ? `${details.tenantFirstname} ${details.tenantLastname ?? ""}`.trim()
      : null) ||
    (details?.renter
      ? `${details.renter.firstname ?? ""} ${details.renter.lastname ?? ""}`.trim()
      : null) ||
    "—";

  const startDate =
    quote?.renewalCommencementDate ||
    quote?.currentDepartureDate ||
    quote?.newArrivalDate ||
    quote?.renewalStartDate ||
    quote?.arrivalDate ||
    quote?.arrival_date ||
    null;
  const endDate =
    quote?.newDepartureDate ||
    quote?.renewalEndDate ||
    quote?.departureDate ||
    quote?.departure_date ||
    (startDate && !Number.isNaN(Date.parse(startDate))
      ? (rentIsMonthly ? addMonths : addYears)(parseISO(startDate), quotedTerm).toISOString()
      : null);

  const annualRent =
    bill?.annualRent ??
    quote?.annualRent ??
    quote?.rentPerYear ??
    bill?.rentPerYear;
  const cautionFee = bill?.cautionFee ?? quote?.cautionFee;
  const serviceFee =
    bill?.serviceChargeTotal ??
    bill?.serviceCharge ??
    quote?.serviceFee ??
    quote?.serviceCharge ??
    bill?.serviceCharge;
  const totalAmount =
    bill?.total ?? quote?.total ?? quote?.totalAmount ?? bill?.total;

  const renewalBookingId = renewal?.transactionId || "";
  const proceedToPayment = async () => {
    if (!id || !canRenewRent(booking) || loading || !quote || unresolvedRenewal || quotedTerm !== rentTerm || creatingRef.current) return;
    if (renewal?.term === rentTerm) { setShowPaymentModal(true); return; }
    creatingRef.current = true;
    setCreating(true);
    setCreationError("");
    try {
      const response = await createRentRenewal(id, rentIsMonthly ? { durationMonths: rentTerm } : { durationYears: rentTerm });
      const paymentTransaction = getRentPaymentTransaction(response);
      if (!paymentTransaction.transactionId || paymentTransaction.transactionId === id) {
        setUnresolvedRenewal(true);
        throw new Error("Renewal created, but its payment transaction is unavailable. Open My Bookings to continue; do not pay the original booking.");
      }
      setRenewal({ ...paymentTransaction, term: rentTerm, total: Number(getRentBill(response)?.total ?? totalAmount) });
      setShowPaymentModal(true);
    } catch (err: any) {
      setCreationError(err.message || "Could not create renewal booking.");
    } finally {
      creatingRef.current = false;
      setCreating(false);
    }
  };

  return (
    <DashboardLayout
      title="Rent Property"
      subtitle="Select and rent property on krent"
    >
      <main className={`${styles["page"]} font-onest`}>
        <button
          type="button"
          className={styles["back"]}
          onClick={() => navigate("/my-bookings?tab=rent")}
        >
          <ArrowLeft size={18} /> Back to bookings
        </button>

        <header className={styles["heading"]}>
          <div className={styles["renewIcon"]} aria-hidden="true">
            <div>
              <RotateCw size={26} strokeWidth={1.6} />
            </div>
          </div>
          <h1>Renew Rent</h1>
        </header>

        <section
          className={styles["card"]}
          aria-busy={loading}
          aria-label="Rent renewal"
        >
          {loading ? (
            <div
              className={styles["skeleton"]}
              role="status"
              aria-label="Loading renewal quote"
            >
              {[100, 60, 80, 50, 70].map((width, index) => (
                <div key={index} style={{ width: `${width}%` }} />
              ))}
              <span className="visually-hidden">Loading renewal quote…</span>
            </div>
          ) : error ? (
            <div className={styles["error"]} role="alert">
              <p>{error}</p>
              <button type="button" onClick={() => fetchQuote(rentTerm, rentIsMonthly ? "months" : "years")}>
                Try again
              </button>
            </div>
          ) : (
            <>
              <div className={styles["property"]}>
                {imageUrl && !propertyImageFailed ? (
                  <img
                    className={styles["photo"]}
                    src={imageUrl}
                    alt={propertyName}
                    onError={() => setPropertyImageFailed(true)}
                  />
                ) : (
                  <div
                    className={`${styles["photo"]} ${styles["placeholder"]}`}
                    aria-label="No property image"
                  >
                    <ImageIcon size={32} />
                  </div>
                )}
                <div className={styles["propertyInfo"]}>
                  <h2>{propertyName}</h2>
                  <p className={styles["location"]}>
                    <MapPin size={15} />
                    {locationStr}
                  </p>
                  <dl className={styles["details"]}>
                    <div className={styles["detail"]}>
                      <CalendarDays size={18} aria-hidden="true" />
                      <div>
                        <dt>Rent Duration</dt>
                        <dd className={styles["duration"]}>
                          <span>{rentIsMonthly ? "Months of stay" : "Years of stay"}</span>
                          <div className={styles["stepper"]}>
                            <button
                              type="button"
                              aria-label={`Decrease ${rentUnit}s`}
                              disabled={rentTerm <= 1 || loading || creating || !!renewal}
                              onClick={decrementTerm}
                            >
                              −
                            </button>
                            <span aria-live="polite">{quotedTerm}</span>
                            <button
                              type="button"
                              aria-label={`Increase ${rentUnit}s`}
                              disabled={
                                loading || creating || quotedTerm !== rentTerm || !!renewal ||
                                (maxTerm != null && rentTerm >= maxTerm)
                              }
                              onClick={incrementTerm}
                            >
                              +
                            </button>
                          </div>
                        </dd>
                      </div>
                    </div>
                    <div className={styles["detail"]}>
                      <CalendarDays size={18} aria-hidden="true" />
                      <div>
                        <dt>Rent Date</dt>
                        <dd>
                          {fmtDate(startDate)} - {fmtDate(endDate)}
                        </dd>
                      </div>
                    </div>
                    <div className={styles["detail"]}>
                      <UsersRound size={18} aria-hidden="true" />
                      <div>
                        <dt>Landlord</dt>
                        <dd>{landlordName}</dd>
                      </div>
                    </div>
                    <div className={styles["detail"]}>
                      <UsersRound size={18} aria-hidden="true" />
                      <div>
                        <dt>Tenant</dt>
                        <dd>{tenantName}</dd>
                      </div>
                    </div>
                  </dl>
                </div>
              </div>

              <section
                className={styles["summary"]}
                aria-labelledby="renewal-payment-summary"
              >
                <h3 id="renewal-payment-summary">Payment summary</h3>
                <dl>
                  <div>
                    <dt>{rentIsMonthly ? "Monthly rent" : "Annual rent"}</dt>
                    <dd>{formatNaira(rentIsMonthly ? bill?.monthlyRent : annualRent)}</dd>
                  </div>
                  {quotedTerm > 1 && (
                    <div>
                      <dt>Rent total ({quotedTerm} {rentUnit}s)</dt>
                      <dd>{formatNaira(bill?.rentTotal)}</dd>
                    </div>
                  )}
                  <div>
                    <dt>Caution fee</dt>
                    <dd>{formatNaira(cautionFee)}</dd>
                  </div>
                  <div>
                    <dt>Service fee</dt>
                    <dd>{formatNaira(serviceFee)}</dd>
                  </div>
                  {bill?.administrativeFee != null && (
                    <div>
                      <dt>Administrative fee</dt>
                      <dd>{formatNaira(bill.administrativeFee)}</dd>
                    </div>
                  )}
                  <div className={styles["total"]}>
                    <dt>Total paid</dt>
                    <dd>{formatNaira(totalAmount)}</dd>
                  </div>
                </dl>
              </section>
              {booking && !canRenewRent(booking) && <p>{renewalUnavailableMessage(booking)}.</p>}
              {creationError && <p role="alert" className="text-danger">{creationError}</p>}
              <button
                type="button"
                className={styles["pay"]}
                disabled={
                  totalAmount == null ||
                  !Number.isFinite(Number(totalAmount)) ||
                  !canRenewRent(booking) || loading || creating || unresolvedRenewal || quotedTerm !== rentTerm
                }
                onClick={proceedToPayment}
              >
                <Wallet
                  size={20}
                  strokeWidth={1.6}
                  className={styles["walletIcon"]}
                  color="#fff"
                />{" "}
                {creating ? "Creating renewal…" : "Proceed to pay"}
              </button>
            </>
          )}
        </section>
        {showPaymentModal && renewal && quote && totalAmount != null && (
          <PaymentMethodModal
            show={showPaymentModal}
            onClose={() => setShowPaymentModal(false)}
            bookingId={renewalBookingId}
            referenceId={renewal?.referenceId}
            totalAmount={renewal?.total ?? Number(totalAmount)}
            currency="₦"
          />
        )}
      </main>
    </DashboardLayout>
  );
}
